import { useState } from 'react'
import { Button } from '@/shared/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card'
import { Badge } from '@/shared/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { Skeleton } from '@/shared/components/ui/skeleton'
import { ClipboardList, RefreshCw, TrendingUp, Users, CheckCircle, XCircle, Info, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Download } from 'lucide-react'
import { RegistrosTable } from '@/modules/medifarma/components/RegistrosTable'
import { RegistrosFiltros } from '@/modules/medifarma/components/RegistrosFiltros'
import { RegistroDetailSheet } from '@/modules/medifarma/components/RegistroDetailSheet'
import { SolicitudesPorProvinciaChart } from '@/modules/medifarma/components/SolicitudesPorProvinciaChart'
import { SolicitudesEstadoChart } from '@/modules/medifarma/components/SolicitudesEstadoChart'
import { useMedifarmaRegistros, useMedifarmaStats, useExportarMedifarma } from '@/modules/medifarma/hooks/useMedifarma'
import { toast } from 'sonner'

export function SeguimientoPage() {
  const [showInfo, setShowInfo] = useState(false)
  const [selectedRegistroId, setSelectedRegistroId] = useState<number | null>(null)
  const [detailSheetOpen, setDetailSheetOpen] = useState(false)
  const [filters, setFilters] = useState({
    page: 1,
    page_size: 25,
    search: '',
    ciudades: undefined as string[] | undefined,
    estados: undefined as string[] | undefined,
  })

  // Queries
  const { data: registrosData, isLoading, refetch } = useMedifarmaRegistros(
    filters.page,
    filters.page_size,
    {
      search: filters.search || undefined,
      ciudades: filters.ciudades,
      estados: filters.estados,
    }
  )

  const exportarMutation = useExportarMedifarma()

  const { data: stats, isLoading: isLoadingStats } = useMedifarmaStats({
    ciudades: filters.ciudades,
    estados: filters.estados,
    search: filters.search || undefined,
  })

  // Handlers
  const handleFiltersChange = (newFilters: any) => {
    setFilters(newFilters)
  }

  const handleClearFilters = () => {
    setFilters({
      page: 1,
      page_size: 25,
      search: '',
      ciudades: undefined,
      estados: undefined,
    })
  }

  const handlePageChange = (newPage: number) => {
    setFilters({ ...filters, page: newPage })
  }

  const handlePageSizeChange = (newPageSize: string) => {
    setFilters({ ...filters, page: 1, page_size: parseInt(newPageSize) })
  }

  const handleViewRegistro = (id: number) => {
    setSelectedRegistroId(id)
    setDetailSheetOpen(true)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <ClipboardList className="h-6 w-6 text-black dark:text-white" />
            Seguimiento De Solicitudes
          </h1>
          <button
            onClick={() => setShowInfo(!showInfo)}
            className="text-muted-foreground hover:text-foreground transition-colors ml-2"
          >
            <Info className="h-5 w-5" />
          </button>
          {showInfo && (
            <span className="text-muted-foreground ml-2 animate-in slide-in-from-left-2 duration-200">
              Gestión y visualización de registros de Medifarma
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              exportarMutation.mutate(
                {
                  search: filters.search || undefined,
                  ciudades: filters.ciudades,
                  estados: filters.estados,
                },
                {
                  onError: () => toast.error('Error al exportar'),
                }
              )
            }}
            disabled={exportarMutation.isPending}
          >
            <Download className={`h-4 w-4 mr-2 ${exportarMutation.isPending ? 'animate-bounce' : ''}`} />
            {exportarMutation.isPending ? 'Exportando...' : 'Exportar Excel'}
          </Button>
          <Button variant="ghost" size="icon" onClick={() => refetch()} disabled={isLoading}>
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card className="border border-gray-300 dark:border-gray-700 bg-gradient-to-r from-teal-500/15 via-teal-500/5 via-50% to-transparent dark:from-teal-800/20 dark:via-teal-900/10 dark:via-50% dark:to-transparent">
          <CardContent className="pt-6">
            {isLoadingStats ? (
              <Skeleton className="h-8 w-20" />
            ) : (
              <div className="flex items-start justify-between">
                <div className="space-y-1">
                  <p className="text-sm font-medium text-muted-foreground">Total Registros</p>
                  <p className="text-3xl font-bold">{stats?.total_registros || 0}</p>
                </div>
                <Users className="h-6 w-6 text-teal-500" />
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border border-gray-300 dark:border-gray-700 bg-gradient-to-r from-teal-500/15 via-teal-500/5 via-50% to-transparent dark:from-teal-800/20 dark:via-teal-900/10 dark:via-50% dark:to-transparent">
          <CardContent className="pt-6">
            {isLoadingStats ? (
              <Skeleton className="h-8 w-20" />
            ) : (
              <div className="flex items-start justify-between">
                <div className="space-y-1">
                  <p className="text-sm font-medium text-muted-foreground">Este Mes</p>
                  <p className="text-3xl font-bold">{stats?.registros_mes || 0}</p>
                </div>
                <TrendingUp className="h-6 w-6 text-teal-500" />
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border border-gray-300 dark:border-gray-700 bg-gradient-to-r from-teal-500/15 via-teal-500/5 via-50% to-transparent dark:from-teal-800/20 dark:via-teal-900/10 dark:via-50% dark:to-transparent">
          <CardContent className="pt-6">
            {isLoadingStats ? (
              <Skeleton className="h-8 w-20" />
            ) : (
              <div className="flex items-start justify-between">
                <div className="space-y-1">
                  <p className="text-sm font-medium text-muted-foreground">Registrados</p>
                  <p className="text-3xl font-bold">{stats?.por_estado?.['Registrado'] || 0}</p>
                </div>
                <CheckCircle className="h-6 w-6 text-teal-500" />
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border border-gray-300 dark:border-gray-700 bg-gradient-to-r from-teal-500/15 via-teal-500/5 via-50% to-transparent dark:from-teal-800/20 dark:via-teal-900/10 dark:via-50% dark:to-transparent">
          <CardContent className="pt-6">
            {isLoadingStats ? (
              <Skeleton className="h-8 w-20" />
            ) : (
              <div className="flex items-start justify-between">
                <div className="space-y-1">
                  <p className="text-sm font-medium text-muted-foreground">Pendientes</p>
                  <p className="text-3xl font-bold">{stats?.por_estado?.['Pendiente'] || 0}</p>
                </div>
                <XCircle className="h-6 w-6 text-teal-500" />
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Graficos */}
      <div className="grid gap-4 md:grid-cols-2">
        <SolicitudesPorProvinciaChart
          data={stats?.por_ciudad}
          isLoading={isLoadingStats}
        />
        <SolicitudesEstadoChart
          data={stats?.por_estado}
          isLoading={isLoadingStats}
        />
      </div>

      {/* Filtros */}
      <RegistrosFiltros
        filters={filters}
        onFiltersChange={handleFiltersChange}
        onClearFilters={handleClearFilters}
        stats={stats}
      />

      {/* Tabla */}
      <Card className="border border-gray-300 dark:border-gray-700">
        <CardHeader>
          <CardTitle className="flex items-center gap-3">
            Solicitudes
            {registrosData && (
              <Badge className="badge-gradient hover:opacity-90">
                {registrosData.count}
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-4">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : registrosData && registrosData.results.length > 0 ? (
            <>
              <RegistrosTable
                registros={registrosData.results}
                onView={handleViewRegistro}
              />

              {/* Paginación */}
              <div className="flex items-center justify-between mt-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">Mostrar</span>
                  <Select
                    value={filters.page_size.toString()}
                    onValueChange={handlePageSizeChange}
                  >
                    <SelectTrigger className="w-[80px] h-8">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="10">10</SelectItem>
                      <SelectItem value="25">25</SelectItem>
                      <SelectItem value="50">50</SelectItem>
                      <SelectItem value="100">100</SelectItem>
                    </SelectContent>
                  </Select>
                  <span className="text-sm text-muted-foreground">por página</span>
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => handlePageChange(1)}
                    disabled={registrosData.page === 1}
                  >
                    <ChevronsLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => handlePageChange(registrosData.page - 1)}
                    disabled={registrosData.page === 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="text-sm text-muted-foreground min-w-[100px] text-center">
                    {registrosData.page} de {registrosData.total_pages}
                  </span>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => handlePageChange(registrosData.page + 1)}
                    disabled={registrosData.page === registrosData.total_pages}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => handlePageChange(registrosData.total_pages)}
                    disabled={registrosData.page === registrosData.total_pages}
                  >
                    <ChevronsRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <ClipboardList className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No se encontraron registros</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Sheet de detalle */}
      <RegistroDetailSheet
        registroId={selectedRegistroId}
        open={detailSheetOpen}
        onOpenChange={setDetailSheetOpen}
      />
    </div>
  )
}
